/* tslint:disable */
/* eslint-disable */
/**
 * Internal Variable Definitions Administration API
 *  ## Introduction  Variable Definitions are centralized definitions of concrete variables which are typically present in multiple datasets. Variable Definitions support standardization of data and metadata and facilitate sharing and joining of data by clarifying when variables have an identical definition.  ## Maintenance of Variable Definitions This API allows for creation, maintenance and access of Variable Definitions.  ### Ownership Creation and maintenance of variables may only be performed by Statistics Norway employees representing a specific Dapla team, who are defined as the owners of a given Variable Definition. The team an owner represents must be specified when making a request through the `active_group` query parameter. All maintenance is to be performed by the owners, with no intervention from administrators.  ### Status All Variable Definitions have an associated status. The possible values for status are `DRAFT`, `PUBLISHED_INTERNAL` and `PUBLISHED_EXTERNAL`.  #### Draft When a Variable Definition is created it is assigned the status `DRAFT`. Under this status the Variable Definition is:  - Only visible to Statistics Norway employees. - Mutable (it may be changed directly without need for versioning). - Not suitable to refer to from other systems.  This status may be changed to `PUBLISHED_INTERNAL` or `PUBLISHED_EXTERNAL` with a direct update.  #### Published Internal Under this status the Variable Definition is:  - Only visible to Statistics Norway employees. - Immutable (all changes are versioned). - Suitable to refer to in internal systems for statistics production. - Not suitable to refer to for external use (for example in Statistikkbanken).  This status may be changed to `PUBLISHED_EXTERNAL` by creating a Patch version.  #### Published External Under this status the Variable Definition is:  - Visible to the general public. - Immutable (all changes are versioned). - Suitable to refer to from any system.  This status may not be changed as it would break immutability. If a Variable Definition is no longer relevant then its period of validity should be ended by specifying a `valid_until` date in a Patch version.  ### Immutability Variable Definitions are immutable. This means that any changes must be performed in a strict versioning system. Consumers can avoid being exposed to breaking changes by specifying a `date_of_validity` when they request a Variable Definition.  #### Patches Patches are for changes which do not affect the fundamental meaning of the Variable Definition.  #### Validity Periods Validity Periods are versions with a period defined by a `valid_from` date and optionally a `valid_until` date. If the fundamental meaning of a Variable Definition is to be changed, it should be done by creating a new Validity Period.  
 *
 * The version of the OpenAPI document: 0.1
 * Contact: metadata@ssb.no
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import { mapValues } from '../runtime';
import type { Owner } from './Owner';
import {
    OwnerFromJSON,
    OwnerFromJSONTyped,
    OwnerToJSON,
    OwnerToJSONTyped,
} from './Owner';
import type { VariableStatus } from './VariableStatus';
import {
    VariableStatusFromJSON,
    VariableStatusFromJSONTyped,
    VariableStatusToJSON,
    VariableStatusToJSONTyped,
} from './VariableStatus';
import type { RenderedContact } from './RenderedContact';
import {
    RenderedContactFromJSON,
    RenderedContactFromJSONTyped,
    RenderedContactToJSON,
    RenderedContactToJSONTyped,
} from './RenderedContact';
import type { KlassReference } from './KlassReference';
import {
    KlassReferenceFromJSON,
    KlassReferenceFromJSONTyped,
    KlassReferenceToJSON,
    KlassReferenceToJSONTyped,
} from './KlassReference';

/**
 * Render a Variable Definition in a specific language, for display to end users.
 * @export
 * @interface RenderedView
 */
export interface RenderedView {
    /**
     * Unique identifier for the variable definition.
     * @type {string}
     * @memberof RenderedView
     */
    id: string;
    /**
     * Integer identifying a patch of a variable definition.
     * @type {number}
     * @memberof RenderedView
     */
    patch_id: number;
    /**
     * Name of the variable. Must be unique for a given Unit Type and Owner combination.
     * @type {string}
     * @memberof RenderedView
     */
    name?: string | null;
    /**
     * Recommended short name. Must be unique within an organization.
     * @type {string}
     * @memberof RenderedView
     */
    short_name: string;
    /**
     * Definition of the variable.
     * @type {string}
     * @memberof RenderedView
     */
    definition?: string | null;
    /**
     * Link to the classification which defines all permitted values for this variable.
     * @type {string}
     * @memberof RenderedView
     */
    classification_uri?: string | null;
    /**
     * 
     * @type {Array<KlassReference>}
     * @memberof RenderedView
     */
    unit_types: Array<KlassReference>;
    /**
     * 
     * @type {Array<KlassReference>}
     * @memberof RenderedView
     */
    subject_fields: Array<KlassReference>;
    /**
     * True if variable instances contain particularly sensitive information. Applies even if the information or identifiers are pseudonymized. Information within the following categories are regarded as particularly sensitive: Ethnicity, Political alignment, Religion, Philosophical beliefs, Union membership, Genetics, Biometrics, Health, Sexual relations, Sexual orientation
     * @type {boolean}
     * @memberof RenderedView
     */
    contains_special_categories_of_personal_data: boolean;
    /**
     * 
     * @type {VariableStatus}
     * @memberof RenderedView
     */
    variable_status: VariableStatus;
    /**
     * 
     * @type {KlassReference}
     * @memberof RenderedView
     */
    measurement_type?: KlassReference | null;
    /**
     * The variable definition is valid from this date inclusive
     * @type {Date}
     * @memberof RenderedView
     */
    valid_from: Date;
    /**
     * The variable definition is valid until this date inclusive
     * @type {Date}
     * @memberof RenderedView
     */
    valid_until?: Date | null;
    /**
     * A link (URI) to an external definition/documentation
     * @type {string}
     * @memberof RenderedView
     */
    external_reference_uri?: string | null;
    /**
     * Optional comment to explain the definition or communicate potential changes.
     * @type {string}
     * @memberof RenderedView
     */
    comment?: string | null;
    /**
     * Link(s) to related definitions of variables - a list of one or more definitions. For example for a variable after-tax income it could be relevant to link to definitions of income from work, property income etc.
     * @type {Array<string>}
     * @memberof RenderedView
     */
    related_variable_definition_uris?: Array<string> | null;
    /**
     * 
     * @type {Owner}
     * @memberof RenderedView
     */
    owner: Owner;
    /**
     * 
     * @type {RenderedContact}
     * @memberof RenderedView
     */
    contact?: RenderedContact | null;
    /**
     * The timestamp at which this variable definition was first created.
     * @type {Date}
     * @memberof RenderedView
     */
    created_at: Date;
    /**
     * The user who created this variable definition.
     * @type {string}
     * @memberof RenderedView
     */
    created_by: string;
    /**
     * The timestamp at which this variable definition was last modified.
     * @type {Date}
     * @memberof RenderedView
     */
    last_updated_at: Date;
    /**
     * The user who last modified this variable definition.
     * @type {string}
     * @memberof RenderedView
     */
    last_updated_by: string;
}



/**
 * Check if a given object implements the RenderedView interface.
 */
export function instanceOfRenderedView(value: object): value is RenderedView {
    if (!('id' in value) || value['id'] === undefined) return false;
    if (!('patch_id' in value) || value['patch_id'] === undefined) return false;
    if (!('short_name' in value) || value['short_name'] === undefined) return false;
    if (!('unit_types' in value) || value['unit_types'] === undefined) return false;
    if (!('subject_fields' in value) || value['subject_fields'] === undefined) return false;
    if (!('contains_special_categories_of_personal_data' in value) || value['contains_special_categories_of_personal_data'] === undefined) return false;
    if (!('variable_status' in value) || value['variable_status'] === undefined) return false;
    if (!('valid_from' in value) || value['valid_from'] === undefined) return false;
    if (!('owner' in value) || value['owner'] === undefined) return false;
    if (!('created_at' in value) || value['created_at'] === undefined) return false;
    if (!('created_by' in value) || value['created_by'] === undefined) return false;
    if (!('last_updated_at' in value) || value['last_updated_at'] === undefined) return false;
    if (!('last_updated_by' in value) || value['last_updated_by'] === undefined) return false;
    return true;
}

export function RenderedViewFromJSON(json: any): RenderedView {
    return RenderedViewFromJSONTyped(json, false);
}

export function RenderedViewFromJSONTyped(json: any, ignoreDiscriminator: boolean): RenderedView {
    if (json == null) {
        return json;
    }
    return {
        
        'id': json['id'],
        'patch_id': json['patch_id'],
        'name': json['name'] == null ? undefined : json['name'],
        'short_name': json['short_name'],
        'definition': json['definition'] == null ? undefined : json['definition'],
        'classification_uri': json['classification_uri'] == null ? undefined : json['classification_uri'],
        'unit_types': ((json['unit_types'] as Array<any>).map(KlassReferenceFromJSON)),
        'subject_fields': ((json['subject_fields'] as Array<any>).map(KlassReferenceFromJSON)),
        'contains_special_categories_of_personal_data': json['contains_special_categories_of_personal_data'],
        'variable_status': VariableStatusFromJSON(json['variable_status']),
        'measurement_type': json['measurement_type'] == null ? undefined : KlassReferenceFromJSON(json['measurement_type']),
        'valid_from': (new Date(json['valid_from'])),
        'valid_until': json['valid_until'] == null ? undefined : (new Date(json['valid_until'])),
        'external_reference_uri': json['external_reference_uri'] == null ? undefined : json['external_reference_uri'],
        'comment': json['comment'] == null ? undefined : json['comment'],
        'related_variable_definition_uris': json['related_variable_definition_uris'] == null ? undefined : json['related_variable_definition_uris'],
        'owner': OwnerFromJSON(json['owner']),
        'contact': json['contact'] == null ? undefined : RenderedContactFromJSON(json['contact']),
        'created_at': (new Date(json['created_at'])),
        'created_by': json['created_by'],
        'last_updated_at': (new Date(json['last_updated_at'])),
        'last_updated_by': json['last_updated_by'],
    };
}

export function RenderedViewToJSON(json: any): RenderedView {
    return RenderedViewToJSONTyped(json, false);
}

export function RenderedViewToJSONTyped(value?: RenderedView | null, ignoreDiscriminator: boolean = false): any {
    if (value == null) {
        return value;
    }

    return {
        
        'id': value['id'],
        'patch_id': value['patch_id'],
        'name': value['name'],
        'short_name': value['short_name'],
        'definition': value['definition'],
        'classification_uri': value['classification_uri'],
        'unit_types': ((value['unit_types'] as Array<any>).map(KlassReferenceToJSON)),
        'subject_fields': ((value['subject_fields'] as Array<any>).map(KlassReferenceToJSON)),
        'contains_special_categories_of_personal_data': value['contains_special_categories_of_personal_data'],
        'variable_status': VariableStatusToJSON(value['variable_status']),
        'measurement_type': KlassReferenceToJSON(value['measurement_type']),
        'valid_from': value['valid_from'].toISOString().substring(0,10),
        'valid_until': value['valid_until'] == null ? value['valid_until'] : value['valid_until'].toISOString().substring(0,10),
        'external_reference_uri': value['external_reference_uri'],
        'comment': value['comment'],
        'related_variable_definition_uris': value['related_variable_definition_uris'],
        'owner': OwnerToJSON(value['owner']),
        'contact': RenderedContactToJSON(value['contact']),
        'created_at': value['created_at'].toISOString(),
        'created_by': value['created_by'],
        'last_updated_at': value['last_updated_at'].toISOString(),
        'last_updated_by': value['last_updated_by'],
    };
}

